import { useEffect, useRef } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Flex } from "@similarweb/ui-components/foundations";

import { useContactWidgetDataContext } from "app/pages/sales-intelligence/modules/ai-agent/context/ContactWidgetDataContext";
import { useGetContacts } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/queries/useGetContacts";
import { useUpdateMessage } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/queries/useUpdateMessage";
import type { ContactWidgetData } from "app/pages/sales-intelligence/modules/ai-agent/types/superAgent";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type {
    ContactSearchEntry,
    CRMEntity,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

import { augmentContactWidgetWithContactsSearchData } from "../../utils/augmentContactWidgetInMessage";

import { ContactWidgetItem } from "./ContactWidgetItem";
import { ContactWidgetSkeleton } from "./ContactWidgetSkeleton";

type Props = {
    widgetData: ContactWidgetData;
    getMessageContent: () => string;
    setMessageContent: (content: string) => void;
    messageId: string;
    isStreaming: boolean;
    onExportToCRM: (contact: ContactSearchEntry, destination: Integration, as?: CRMEntity) => void;
};

export const ContactWidget = ({
    widgetData,
    getMessageContent,
    setMessageContent,
    messageId,
    isStreaming,
    onExportToCRM,
}: Props) => {
    const setContactWidgetData = useContactWidgetDataContext()?.setContactWidgetData;

    const {
        params: { id: superAgentThreadId },
    } = useCurrentStateAndParams<{ id: string }>();
    const { mutate: updateSuperAgentMessage } = useUpdateMessage();

    const lastSentContactsPayloadRef = useRef<string | null>(null);

    /** Stable for effect deps: parent often passes a new array ref with the same contact ids. */
    const widgetIdsKey = widgetData.join(",");

    useEffect(() => {
        setContactWidgetData?.(widgetData);
    }, [widgetData, setContactWidgetData]);

    useEffect(() => {
        lastSentContactsPayloadRef.current = null;
    }, [widgetIdsKey, messageId]);

    const { data, isLoading } = useGetContacts(widgetData);

    useEffect(() => {
        // Defer augmentation until streaming completes. The augmenter requires a closing ```
        // fence to locate the JSON block; during streaming the widget mounts as soon as the JSON
        // becomes parseable (allowTrailing), but the closing fence may not have arrived yet.
        // Running here would produce an unchanged message and could persist a partial message
        // to the backend via updateSuperAgentMessage.
        if (isStreaming || isLoading || !data || !superAgentThreadId || !messageId || !getMessageContent()) {
            return;
        }
        const fingerprint = JSON.stringify(data);
        if (lastSentContactsPayloadRef.current === fingerprint) {
            return;
        }
        lastSentContactsPayloadRef.current = fingerprint;
        const augmented = augmentContactWidgetWithContactsSearchData(getMessageContent(), data);
        setMessageContent(augmented);
        if (salesSettingsHelper.isSuperAgentEnabled) {
            updateSuperAgentMessage({
                threadId: superAgentThreadId,
                messageId,
                message: augmented,
            });
        }
    }, [
        data,
        isLoading,
        isStreaming,
        messageId,
        superAgentThreadId,
        updateSuperAgentMessage,
        getMessageContent,
        setMessageContent,
    ]);

    if (isLoading) {
        return <ContactWidgetSkeleton count={widgetData.length} />;
    }

    return (
        <Flex
            gap="16px"
            flexWrap="wrap"
            data-automation="contact-widget-list"
        >
            {data?.rows?.map((contact) => (
                <ContactWidgetItem
                    key={contact.id}
                    contact={contact}
                    onExportToCRM={onExportToCRM}
                    widgetData={widgetData}
                />
            ))}
        </Flex>
    );
};
